import React, { useEffect, useState, useRef } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { t } from "i18next";
import {
  Button,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
  Card,
  CardBody,
} from "reactstrap";
import { FormControlLabel, Switch } from "@mui/material";
import { useSpaceStore } from "../../../../stores/Assets/space";
import { useUsageStore } from "../../../../stores/Usage";
import { SketchPicker } from "react-color";
import Select from "react-select";
import Dragula from "react-dragula";
import "dragula/dist/dragula.css";
import { useAuth } from "../../../../stores";
import {
  canFullOrder,
  getMaxServiceLevel,
  getServiceLevelOptions,
} from "../../../../helpers/planLimits";

const validTypes = ["OFFICE", "MAJLISS", "RESTAURANT", "RESTAURANT_2", "BEACH"];
const languages = [
  {
    label: "ENGLISH",
    value: "EN",
  },
  {
    label: "ARABIC",
    value: "AR",
  },
];



const EditSpace = ({
  rowData,
  kitchenList,
  menuList,
  siteList,
  showEditSpaceModal,
  toggleEditSpaceModal,
}) => {
  const [selectedSiteId, setSelectedSiteId] = useState(
    siteList[0]?.site_id || null
  );

  const [selectedKichenId, setSelectedKichenId] = useState(null);
  const [selectedSecondaryKitchenId, setSelectedSecondaryKitchenId] = useState("");
  const [selectedMenuIds, setSelectedMenuIds] = useState([]); // Changed to array for multi-select
  const { updateSpace, isLoading } = useSpaceStore((state) => state);

  const [currKitchens, setCurrentKitchens] = useState([]);
  const [currMenus, setCurrMenus] = useState([]);
  const [theme1, setTheme1] = useState("");
  const [theme2, setTheme2] = useState("");
  const [saveLastOrder, setSaveLastOrder] = useState(true);
  const [call_active, setCallActive] = useState(false);
  const [showFeedback, setShowFeedback] = useState(true);
  const [service_level, setServiceLevel] = useState(3);
  const [useCustomTheme, setUseCustomTheme] = useState(false);
  const { usage, fetchUsage } = useUsageStore();
  const user = useAuth((state) => state.user);
  // Drag and drop ref
  const dragulaContainerRef = useRef(null);
const [serviceLevelOptions, setServiceLevelOptions] = useState(
  getServiceLevelOptions(3)
);
  useEffect(() => {
      // Fetch usage to determine service level options
      fetchUsage();
      // Dynamically set service level options based on usage limits

  }, [fetchUsage]);
  useEffect(() => {
    const maxLevel =
      user?.role === "ROOT" ? 3 : getMaxServiceLevel(usage);
    setServiceLevelOptions(getServiceLevelOptions(maxLevel));
  }, [usage, user]);
  useEffect(() => {
    if (selectedSiteId) {
      const filteredKitchens = kitchenList.filter(
        (kitchen) => kitchen?.site?.site_id === parseInt(selectedSiteId)
      );
      const filteredMenus = menuList.filter((menu) => {
        if (!Array.isArray(menu?.sites)) return false;
        return menu.sites.some(
          (site) => parseInt(site.site_id) === parseInt(selectedSiteId)
        );
      });
      setCurrMenus(filteredMenus);
      setCurrentKitchens(filteredKitchens);
    }
  }, [selectedSiteId]);

  // Initialize Dragula for drag and drop
  useEffect(() => {
    if (dragulaContainerRef.current) {
      const drake = Dragula([dragulaContainerRef.current], {
        moves: function (el, container, handle) {
          return true;
        },
      });

      drake.on("drop", function (el, target, source, sibling) {
        // Get the new order of menu IDs from the DOM
        const newOrder = Array.from(dragulaContainerRef.current.children).map(
          (child) => parseInt(child.getAttribute("data-menu-id"))
        );
        setSelectedMenuIds(newOrder);
      });

      return () => {
        drake.destroy();
      };
    }
  }, [selectedMenuIds.length]);

  useEffect(() => {
    if (rowData) {
      // Set initial values if rowData changes
      formik.setValues({
        name: rowData?.name || "",
        name_ar: rowData?.name_ar || "",
        default_lang: rowData?.default_lang || "",
        service_level: rowData?.service_level || false,
        type: rowData?.type || "",
        theme: rowData?.theme,
        file: null,
      });

      // Set selected IDs directly from rowData
      setSelectedKichenId(parseInt(rowData?.kitchen_id));
      setSelectedSecondaryKitchenId(
        rowData?.secondary_kitchen_id
          ? String(rowData.secondary_kitchen_id)
          : ""
      );
      setSelectedSiteId(parseInt(rowData?.site_id));

      // Handle menu IDs - convert to array if it's a single value
      if (rowData?.menu_ids && Array.isArray(rowData?.menu_ids)) {
        setSelectedMenuIds(rowData?.menu_ids.map((id) => parseInt(id)));
      } else if (rowData?.menu_id) {
        // If the API returns a single menu_id, convert it to array
        setSelectedMenuIds([parseInt(rowData?.menu_id)]);
      } else {
        setSelectedMenuIds([]);
      }

      const hasTheme = Boolean(rowData?.theme);
      setUseCustomTheme(hasTheme);

      if (hasTheme && rowData?.theme?.includes(",")) {
        const [firstTheme, secondTheme] = rowData.theme.split(",");
        setTheme1(firstTheme);
        setTheme2(secondTheme);
      } else if (hasTheme) {
        setTheme1(rowData?.theme);
        setTheme2("");
      } else {
        setTheme1("");
        setTheme2("");
      }

      // Set saveLastOrder from rowData or default to true
      setSaveLastOrder(
        rowData?.saveLastOrder !== undefined ? rowData?.saveLastOrder : true
      );
      setCallActive(rowData?.call_active !== undefined ? rowData?.call_active : false);
      setShowFeedback(rowData?.showFeedback);
      setServiceLevel(
        rowData?.service_level !== undefined && rowData?.service_level !== null
          ? rowData.service_level
          : 3
      );
    }
  }, [rowData]);

  const formik = useFormik({
    initialValues: {
      name: "",
      name_ar: "",
      default_lang: "",
      type: "",
      theme: "",
    },
    // validationSchema: Yup.object({
    //   name: Yup.string().optional(),
    //   name_ar: Yup.string().optional(),
    //   default_lang: Yup.string().optional(),
    //   theme: Yup.string()
    //     .matches(/^#[0-9A-Fa-f]{6}$/, t("Invalid hex color"))
    //     .optional(),
    //   type: Yup.string().oneOf(validTypes, t("Invalid type")).optional(),
    // }),
    onSubmit: async (values) => {
      let theme = "";
      if (useCustomTheme) {
        if (theme1 && theme2) {
          theme = `${theme1},${theme2}`;
        } else if (theme1) {
          theme = theme1;
        } else if (theme2) {
          theme = theme2;
        }
      }

      // Filter out null values from selectedMenuIds before sending
      const filteredMenuIds = selectedMenuIds.filter(id => id !== null && id !== undefined);

      const spaceData = {
        name: values.name,
        name_ar: values.name_ar,
        default_lang: values.default_lang,
        type: values.type,
        theme: theme,
        site_id: parseInt(selectedSiteId),
        menu_ids: filteredMenuIds, // Send filtered array of menu IDs
        kitchen_id: parseInt(selectedKichenId),
        secondary_kitchen_id: selectedSecondaryKitchenId
          ? parseInt(selectedSecondaryKitchenId)
          : null,
        saveLastOrder: saveLastOrder,
        // call_active: call_active,
        showFeedback: showFeedback,
        service_level: service_level,
      };

      const result = await updateSpace(
        rowData?.space_id,
        spaceData,
        parseInt(selectedSiteId)
      );
      if (result) {
        formik.resetForm();
        setSelectedSiteId(siteList[0]?.site_id || null);
        setSelectedMenuIds([]);
        toggleEditSpaceModal();
      }
    },
  });

  return (
    <Modal isOpen={showEditSpaceModal} toggle={toggleEditSpaceModal}>
      <ModalHeader toggle={toggleEditSpaceModal}>
        {t("Update Space")}
      </ModalHeader>
      <ModalBody>
        <form
          onSubmit={formik.handleSubmit}
          className="d-flex flex-column flex-wrap"
        >
          <div className="flex-fill mb-2">
            <Label for="site">{t("Select Site")}</Label>
            <select
              id="site"
              onChange={(e) => setSelectedSiteId(e.target.value)}
              value={selectedSiteId}
              className="form-control"
            >
              <option value="">Select</option>
              {siteList.map((site) => (
                <option key={site.site_id} value={site.site_id}>
                  {site.site_id} - {site.name}
                </option>
              ))}
            </select>
          </div>
          {selectedSiteId && selectedKichenId && (
            <>
              <div className="flex-fill mb-2">
                <Label for="Kitchen">{t("Select Kitchen")}</Label>
                  <select
                    id="Kitchen"
                    onChange={(e) => {
                      const nextKitchenId = parseInt(e.target.value);
                      setSelectedKichenId(nextKitchenId);
                      if (String(selectedSecondaryKitchenId) === String(nextKitchenId)) {
                        setSelectedSecondaryKitchenId("");
                      }
                    }}
                    value={selectedKichenId || ""}
                    className="form-control"
                  >
                    {currKitchens.map((kitchen) => (
                      <option key={kitchen.kitchen_id} value={kitchen.kitchen_id}>
                        {kitchen.kitchen_id} - {kitchen.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="flex-fill mb-2">
                  <Label for="SecondaryKitchen">{t("Secondary Kitchen")}</Label>
                  <select
                    id="SecondaryKitchen"
                    onChange={(e) => setSelectedSecondaryKitchenId(e.target.value)}
                    value={selectedSecondaryKitchenId}
                    className="form-control"
                  >
                    <option value="">{t("None")}</option>
                    {currKitchens
                      .filter(
                        (kitchen) =>
                          Number(kitchen.kitchen_id) !== Number(selectedKichenId),
                      )
                      .map((kitchen) => (
                        <option
                          key={kitchen.kitchen_id}
                          value={kitchen.kitchen_id}
                        >
                          {kitchen.kitchen_id} - {kitchen.name}
                        </option>
                      ))}
                  </select>
                </div>

                <div className="flex-fill mb-2">
                  <Label for="Menu">{t("Select Menu")}</Label>
                <Select
                  id="Menu"
                  isMulti
                  isDisabled={isLoading}
                  isLoading={isLoading}
                  options={
                    currMenus?.length > 0 &&
                    currMenus.map((menu) => ({
                      value: parseInt(menu.menu_id),
                      label: `${menu.menu_id} - ${menu.name} ${menu.alias ? `(${menu.alias})` : ""
                        }`,
                    }))
                  }
                  value={
                    selectedMenuIds
                      .map((id) =>
                        currMenus?.find(
                          (menu) => parseInt(menu.menu_id) === id
                        )
                      )
                      .filter(Boolean)
                      .map((menu) => ({
                        value: parseInt(menu.menu_id),
                        label: `${menu.menu_id} - ${menu.name} ${menu.alias ? `(${menu.alias})` : ""
                          }`,
                      }))
                  }
                  onChange={(selectedOptions) => {
                    if (selectedOptions) {
                      const nextIds = selectedOptions
                        .map((option) => parseInt(option.value))
                        .filter((id) => !isNaN(id) && id !== null);
                      const kept = selectedMenuIds.filter((id) =>
                        nextIds.includes(id)
                      );
                      const added = nextIds.filter(
                        (id) => !selectedMenuIds.includes(id)
                      );
                      setSelectedMenuIds([...kept, ...added]);
                    } else {
                      setSelectedMenuIds([]);
                    }
                  }}
                  className="basic-multi-select"
                  classNamePrefix="select"
                />
              </div>

              {selectedMenuIds.length > 0 && (
                <div className="flex-fill mb-2">
                  <Label>{t("Reorder Menus (Drag to Reorder)")}</Label>
                  <div
                    ref={dragulaContainerRef}
                    className="menu-order-container"
                    style={{
                      minHeight: "50px",
                      padding: "10px",
                      backgroundColor: "#f8f9fa",
                      borderRadius: "4px",
                      border: "1px dashed #dee2e6",
                    }}
                  >
                    {selectedMenuIds.map((menuId) => {
                      const menu = currMenus?.find(
                        (m) => parseInt(m.menu_id) === menuId
                      );
                      return menu ? (
                        <Card
                          key={menuId}
                          data-menu-id={menuId}
                          className="mb-2"
                          style={{
                            cursor: "move",
                            userSelect: "none",
                          }}
                        >
                          <CardBody className="p-2 d-flex align-items-center justify-content-between">
                            <div className="d-flex align-items-center">
                              <i className="ri-drag-move-fill me-2 text-muted"></i>
                              <span>
                                {menu.menu_id} - {menu.name} {menu.alias ? `(${menu.alias})` : ''}
                              </span>
                            </div>
                            <Button
                              size="sm"
                              color="danger"
                              outline
                              onClick={() => {
                                setSelectedMenuIds(
                                  selectedMenuIds.filter((id) => id !== menuId)
                                );
                              }}
                            >
                              <i className="ri-close-line"></i>
                            </Button>
                          </CardBody>
                        </Card>
                      ) : null;
                    })}
                  </div>
                </div>
              )}
            </>
          )}

          <div className="flex-fill mb-2">
            <Label for="name">{t("Name")}</Label>
            <input
              type="text"
              id="name"
              name="name"
              onChange={formik.handleChange}
              value={formik.values.name}
              className="form-control"
            />
            {formik.errors.name && (
              <div className="text-danger">{formik.errors.name}</div>
            )}
          </div>
          <div className="flex-fill mb-2">
            <Label for="name_ar">{t("Name (AR)")}</Label>
            <input
              type="text"
              id="name_ar"
              name="name_ar"
              onChange={formik.handleChange}
              value={formik.values.name_ar}
              className="form-control"
            />
            {formik.errors.name_ar && (
              <div className="text-danger">{formik.errors.name_ar}</div>
            )}
          </div>
          <div className="flex-fill mb-2">
            <Label for="default_lang">{t("Default Language")}</Label>
            <select
              id="default_lang"
              name="default_lang"
              onChange={formik.handleChange}
              value={formik.values.default_lang}
              className="form-control"
            >
              <option value="">Select</option>
              {languages.map((type) => (
                <option
                  key={type.value}
                  value={type.value}
                  label={type.label}
                />
              ))}
            </select>
            {formik.errors.default_lang && (
              <div className="text-danger">{formik.errors.default_lang}</div>
            )}
          </div>

          <div className="flex-fill mb-2">
            <Label for="type">{t("Type")}</Label>

            <select
              id="type"
              name="type"
              onChange={formik.handleChange}
              value={formik.values.type}
              className="form-control"
            >
              <option value="">Select</option>
              {validTypes.map((type) => (
                <option key={type} value={type} label={type} />
              ))}
            </select>
            {formik.errors.type && (
              <div className="text-danger">{formik.errors.type}</div>
            )}
          </div>
          {/* {useUsageStore.getState().usage?.limits?.can_splash && (
            <div className="flex-fill mb-2">
              <FormControlLabel
                control={
                  <Switch
                    checked={call_active}
                    onChange={(event) => setCallActive(event.target.checked)}
                  />
                }
                label={t("Call Teaboy")}
              />
            </div>
          )} */}
          {canFullOrder(usage) && (
            <div className="flex-fill mb-2">
              <FormControlLabel
                control={
                  <Switch
                    checked={saveLastOrder}
                    onChange={(event) => setSaveLastOrder(event.target.checked)}
                  />
                }
                label={t("Save Last Order")}
              />
            </div>
          )}
          <div className="flex-fill mb-2">
            <FormControlLabel
              control={
                <Switch
                  checked={showFeedback}
                  onChange={(event) => setShowFeedback(event.target.checked)}
                />
              }
              label={t("Show Feedback section")}
            />
          </div>
         
            <div className="flex-fill mb-2">
              <Label for="service_level">{t("Service Level")}</Label>
              <select
                id="service_level"
                name="service_level"
                onChange={(e) => setServiceLevel(parseInt(e.target.value))}
                value={service_level}
                className="form-control"
              >
                {serviceLevelOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {t(option.label)}
                  </option>
                ))}
              </select>
            </div>
          
          <div className="flex-fill mb-2">
            <FormControlLabel
              control={
                <Switch
                  checked={useCustomTheme}
                  onChange={(event) => {
                    const enabled = event.target.checked;
                    setUseCustomTheme(enabled);
                    if (!enabled) {
                      setTheme1("");
                      setTheme2("");
                    }
                  }}
                />
              }
              label={t("Use Custom Theme Colors")}
            />
          </div>

          {useCustomTheme && (
            <div className="d-flex flex-row justify-content-between gap-2">
              <div className="flex-fill mb-2">
                <Label for="theme">{t("Theme Color 1 ")}</Label>
                <SketchPicker
                  color={theme1}
                  onChange={(color) => setTheme1(color.hex)}
                />
              </div>
              <div className="flex-fill mb-2">
                <Label for="theme">{t("Theme Color 2 ")}</Label>
                <SketchPicker
                  color={theme2}
                  onChange={(color) => setTheme2(color.hex)}
                />
              </div>
            </div>
          )}

          <div className="d-flex flex-row align-items-center justify-content-center gap-2 mt-2">
            <Button type="submit" color="success" disabled={isLoading}>
              {isLoading ? (
                <Spinner size="sm" />
              ) : (
                <span>{t("Edit Space")}</span>
              )}
            </Button>
            <Button
              color="danger"
              onClick={() => {
                toggleEditSpaceModal();
                formik.resetForm();
                setSelectedSiteId(siteList[0]?.site_id || null);
                setSelectedMenuIds([]);
              }}
              disabled={isLoading}
            >
              {t("Cancel")}
            </Button>
          </div>
        </form>
      </ModalBody>
      <ModalFooter>{/* Additional footer content can go here */}</ModalFooter>
    </Modal>
  );
};

export default EditSpace;
